import React from "react";
import { FileText, Calendar, User, Tag } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { Progress } from "@shared/ui/progress";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface SopInfoSectionProps {
  sopDetails: any;
}

const SopInfoSection: React.FC<SopInfoSectionProps> = ({ sopDetails }) => {
  console.log(sopDetails);
  // Calculate some derived values
  const usageProgress = Math.min((sopDetails.usageCount / 50) * 100, 100);
  const completionRate = 96; // This could come from API in the future

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <SopDetailsCard sopDetails={sopDetails} />
      <SopStatisticsCard
        sopDetails={sopDetails}
        usageProgress={usageProgress}
        completionRate={completionRate}
      />
    </div>
  );
};

const SopDetailsCard: React.FC<{ sopDetails: any }> = ({ sopDetails }) => {
  return (
    <Card className="col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center">
          <FileText className="mr-2 h-5 w-5 text-primary" />
          SOP Details
        </CardTitle>
        <CardDescription>
          Comprehensive information about this SOP template
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <DetailItem label="ID" value={sopDetails.serviceNo} />

          <DetailItem
            label="Version"
            value={<Badge variant="secondary">{sopDetails.version}</Badge>}
          />

          <DetailItem
            label="Status"
            value={
              <Badge
                variant={
                  sopDetails.statusDisplay === "ACTIVE"
                    ? "default"
                    : "secondary"
                }
              >
                {sopDetails.statusDisplay}
              </Badge>
            }
          />

          <DetailItem label="Type" value={sopDetails.sopTypeDisplay} />
          <DetailItem label="Subtype" value={sopDetails.sopSubtypeDisplay} />

          <DetailItem
            label="Association"
            value={
              <Badge variant="outline" className="bg-slate-50">
                {sopDetails.sopAssociationTypeDisplay}
              </Badge>
            }
          />

          <DetailItem
            label="Utility Services"
            value={
              <div className="flex flex-wrap gap-1">
                {sopDetails.utilityServices?.map((service, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="bg-blue-50 text-blue-700"
                  >
                    {service}
                  </Badge>
                ))}
              </div>
            }
          />

          {/* <DetailItem
            label="Priority"
            value={
              <Badge
                variant={sopDetails.priority === 1 ? "destructive" : "default"}
              >
                {sopDetails.priorityDisplay}
              </Badge>
            }
          /> */}

          <DetailItem
            label="Created At"
            value={
              <p className="font-medium flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 text-blue-500" />
                {new Date(sopDetails.createdDate).toLocaleDateString()}
              </p>
            }
          />

          <DetailItem
            label="Created By"
            value={
              <p className="font-medium flex items-center gap-1">
                <User className="h-3.5 w-3.5 text-blue-500" />
                {sopDetails.createdUserRemoteName}
              </p>
            }
          />

          <DetailItem
            label="Total Tasks"
            value={
              <p className="font-medium flex items-center gap-1">
                <Tag className="h-3.5 w-3.5 text-blue-500" />
                {sopDetails.taskCount}
              </p>
            }
          />

          {/* <DetailItem
            label="Rules Duration"
            value={
              <div className="flex flex-col space-y-1">
                <span className="text-sm font-medium">
                  {sopDetails.rulesDuration}h
                </span>
                <div className="w-full">
                  <Progress
                    value={Math.min((sopDetails.rulesDuration / 72) * 100, 100)}
                    className="h-1.5"
                  />
                </div>
              </div>
            }
          /> */}
        </div>
      </CardContent>
    </Card>
  );
};

const SopStatisticsCard: React.FC<{
  sopDetails: any;
  usageProgress: number;
  completionRate: number;
}> = ({ sopDetails, usageProgress, completionRate }) => {
  const currency = useCurrency();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Statistics</CardTitle>
        <CardDescription>Usage metrics for this SOP template</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <StatisticItem
          label="Usage Count"
          value={
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              {sopDetails.usageCount}
            </Badge>
          }
          progress={usageProgress}
          progressLabel={`${usageProgress.toFixed(0)}% of average usage`}
        />

        <StatisticItem
          label="Total Cost"
          value={
            <span className="font-medium">{currency} {sopDetails.estTotalCost}</span>
          }
        />

        <StatisticItem
          label="Staff Required"
          value={
            <div className="flex items-center gap-1">
              <User className="h-3.5 w-3.5 text-blue-500" />
              <span className="font-medium">{sopDetails.staffRequired}</span>
            </div>
          }
        />

        {/* <StatisticItem
          label="Completion Rate"
          value={<span className="font-medium">{completionRate}%</span>}
          progress={completionRate}
        /> */}
      </CardContent>
    </Card>
  );
};

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      {typeof value === "string" ? (
        <p className="font-medium">{value}</p>
      ) : (
        value
      )}
    </div>
  );
};

const StatisticItem: React.FC<{
  label: string;
  value: React.ReactNode;
  progress?: number;
  progressLabel?: string;
}> = ({ label, value, progress, progressLabel }) => {
  return (
    <div className="space-y-1">
      <div className="flex justify-between">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        {value}
      </div>
      {progress !== undefined && (
        <>
          <Progress value={progress} className="h-1.5" max={100} />
          {progressLabel && (
            <p className="text-xs text-muted-foreground">{progressLabel}</p>
          )}
        </>
      )}
    </div>
  );
};

export default SopInfoSection;
